import { noddiAsync, URLKeys } from 'noddi-async';
import type { NoddiTableTranslations, TabItem, TableAction } from 'noddi-ui-web';
import {
  ApiErrorPageInternal,
  LoadingScreen,
  NoddiBasicCard,
  NoddiButton,
  NoddiTabs,
  useIsMobile,
  useIsWithinTailWindBreakpoint,
  usePagination
} from 'noddi-ui-web';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AddOrganizationMemberDialog } from './AddOrganizationMemberDialog';
import { CarsNeedingAttentionBanner } from './CarsNeedingAttentionBanner';
import { NotificationCarsTable, NotificationMembersTable } from './NotificationCarsTable';
import { getCarColumns, getMemberColumns } from './notificationColumns';
import type { CarGroup, MemberGroup } from './notificationGroupTypes';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { defaultNotificationGroupOverviewTranslations } from './notificationTranslations';
import { OrganizationContactOverlays } from './OrganizationContactOverlays';
import { OrganizationPrimaryContactForm } from './OrganizationPrimaryContactForm';
import { useNotificationOverviewMembers } from './useNotificationOverviewMembers';

type ContactsSubTab = 'members' | 'cars';

// Not `tab`: a host page can have its own tabs on that param, and the two would fight.
const CONTACTS_TAB_PARAM = 'contacts-tab';

interface CarContactsOverviewProps {
  userGroupId: number;
  brandId?: number;
  currentUserId?: number;
  translations?: Partial<NotificationGroupOverviewTranslations>;
  tableTranslations?: NoddiTableTranslations;
  /**
   * The customer app raises no global mutation toast (see its `noddiAsync.init`),
   * so it asks this feature for its own success copy. The backoffice apps already
   * toast every mutation centrally and would double up.
   */
  showMutationToasts?: boolean;
  /**
   * For hosts whose page chrome already shows the title. The description and the add
   * button still render above the card so they sit in the host's header area.
   */
  hideTitle?: boolean;
  /**
   * True for the backoffice apps, whose users hold `is_staff`/`is_manager` and may
   * therefore edit any member's account. Never inferred from a missing `currentUserId`:
   * a partner user is a manager, not staff, and the two must not be conflated.
   */
  viewerIsBackoffice?: boolean;
  /** Whether this host has already established group-admin access. */
  viewerCanManageMembers?: boolean;
  /**
   * Leasing company or car dealership. Members can be marked as employees, which is who
   * may open the organization in the customer app.
   */
  canAssignMembershipRole?: boolean;
  /**
   * Supplied by hosts that have a page per member; the member sheet then offers a
   * link to it, and the driver picker links each candidate. Without it the sheet's
   * footer is Save alone. Takes only the userId so both member shapes can use it.
   */
  getMemberDetailUrl?: (member: Pick<MemberGroup, 'userId'>) => string;
  /**
   * Appended to the member row menu. Host-specific support actions live here so this
   * feature stays free of any one app's routes and lookups.
   */
  extraMemberActions?: TableAction<MemberGroup>[];
  /** Same as `extraMemberActions`, for the car table's row menu. */
  extraCarActions?: TableAction<CarGroup>[];
  /**
   * Called when the viewer left the group. Hosts where that is possible (the customer app)
   * must refresh the session and leave this page: the group is gone from under it.
   */
  onSelfRemoved?: () => void;
}

export const CarContactsOverview = ({
  userGroupId,
  brandId,
  currentUserId,
  translations,
  tableTranslations,
  showMutationToasts = false,
  hideTitle = false,
  viewerIsBackoffice = false,
  viewerCanManageMembers = false,
  canAssignMembershipRole = false,
  getMemberDetailUrl,
  extraMemberActions = [],
  extraCarActions = [],
  onSelfRemoved
}: CarContactsOverviewProps) => {
  const t = { ...defaultNotificationGroupOverviewTranslations, ...translations };
  const isMobile = useIsMobile();
  // The car table is two columns, so it stays readable far below the shared `md` mobile
  // cutoff; only a phone-width screen needs car and driver stacked into one cell.
  const stackCarColumns = useIsWithinTailWindBreakpoint('xs');
  const {
    data: summary,
    isPending: isSummaryPending,
    error: summaryError
  } = noddiAsync.useGet({
    type: URLKeys.getNotificationOverviewSummary,
    input: { userGroupId }
  });
  const carTable = usePagination();
  const memberTable = usePagination();
  const [clickedMember, setClickedMember] = useState<MemberGroup>();
  const { data: memberPage } = useNotificationOverviewMembers(
    userGroupId,
    memberTable.pagination,
    clickedMember !== undefined
  );
  // The clicked row is a snapshot. Read back from the table's page, which every member mutation
  // refetches (the cars review's "Turn on SMS" included), so the sheet's toggles and review follow.
  const memberToEdit =
    clickedMember &&
    (memberPage?.results.find((member) => member.userGroupMemberId === clickedMember.userGroupMemberId) ??
      clickedMember);
  const [carToEdit, setCarToEdit] = useState<CarGroup>();
  const [showOnlyNeedsAttention, setShowOnlyNeedsAttention] = useState(false);
  const filterCarsNeedingAttention = (needsAttention: boolean) => {
    setShowOnlyNeedsAttention(needsAttention);
    // The filtered list can end before the page the table is on, which then showed empty.
    carTable.setPageIndex(0);
  };
  const [memberToRemove, setMemberToRemove] = useState<MemberGroup>();
  const [memberRemoveOpen, setMemberRemoveOpen] = useState(false);
  const [carToRemove, setCarToRemove] = useState<CarGroup>();
  const [carRemoveOpen, setCarRemoveOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // A group admin manages memberships, never a fellow member's account: the name,
  // phone and email are also that person's private Noddi profile.
  const canEditMemberAccount = (member: MemberGroup) => viewerIsBackoffice || member.userId === currentUserId;
  const canManageMembers = viewerIsBackoffice || viewerCanManageMembers;
  if (isSummaryPending) {
    return <LoadingScreen />;
  }
  if (summaryError) {
    return <ApiErrorPageInternal error={summaryError} />;
  }
  const carColumns = getCarColumns(t, stackCarColumns);
  const memberColumns = getMemberColumns(t, isMobile, {
    showPrimaryContact: viewerIsBackoffice,
    showEmployeeRole: canAssignMembershipRole
  });

  // Leaving the group yourself is always allowed; the dialog explains when the
  // last admin has to hand the role over first.
  const canRemove = (member: MemberGroup) => canManageMembers || member.userId === currentUserId;
  // Row click opens the member. Remove sits on the row menu (right-click and the
  // trailing button) so an admin can take someone out without opening the sheet.
  const removeMemberAction: TableAction<MemberGroup> = {
    label: t.removeMember,
    actionType: 'remove',
    disabled: (member) => !canRemove(member),
    onClick: (member) => {
      // The row menu still holds the body scroll lock during this click. Opening the
      // dialog in that same turn makes it remember pointer-events: none as the restored value.
      window.setTimeout(() => {
        setMemberToRemove(member);
        setMemberRemoveOpen(true);
      }, 0);
    }
  };
  const memberActions: TableAction<MemberGroup>[] =
    canManageMembers || currentUserId !== undefined ? [...extraMemberActions, removeMemberAction] : extraMemberActions;
  // Same menu as the member rows: right-click and the trailing button. Only an admin
  // can take a car off the organization, and the dialog records why.
  const removeCarAction: TableAction<CarGroup> = {
    label: t.removeCar,
    actionType: 'remove',
    onClick: (car) => {
      // Same deferral as remove member: let the row menu release the scroll lock first.
      window.setTimeout(() => {
        setCarToRemove(car);
        setCarRemoveOpen(true);
      }, 0);
    }
  };
  const carActions: TableAction<CarGroup>[] = canManageMembers
    ? [...extraCarActions, removeCarAction]
    : extraCarActions;

  // Cars first: every car needs a driver, so the car view is where a new account starts.
  const tabItems: TabItem<ContactsSubTab>[] = [
    {
      label: t.byCar,
      value: 'cars',
      icon: 'Car',
      node: (
        <div className='flex flex-col gap-3'>
          <p className='text-sm text-muted-foreground'>{t.byCarDescription}</p>
          {showOnlyNeedsAttention && (
            <div className='flex flex-wrap items-center gap-2'>
              <p className='text-sm font-medium'>{t.filteredToCarsNeedingAttention}</p>
              <NoddiButton variant='ghost' size='small' onClick={() => filterCarsNeedingAttention(false)}>
                {t.showAllCars}
              </NoddiButton>
            </div>
          )}
          <NotificationCarsTable
            userGroupId={userGroupId}
            pagination={carTable.pagination}
            setPagination={carTable.setPagination}
            needsAttention={showOnlyNeedsAttention}
            columns={carColumns}
            actions={carActions}
            onRowClick={setCarToEdit}
            searchPlaceholder={t.searchCarsTablePlaceholder}
            translations={tableTranslations}
            showCopyRowId={viewerIsBackoffice}
          />
        </div>
      )
    },
    {
      label: t.byMember,
      value: 'members',
      icon: 'Users',
      node: (
        <div className='flex flex-col gap-3'>
          <p className='text-sm text-muted-foreground'>{t.byMemberDescription}</p>
          <NotificationMembersTable
            userGroupId={userGroupId}
            pagination={memberTable.pagination}
            setPagination={memberTable.setPagination}
            columns={memberColumns}
            actions={memberActions}
            onRowClick={setClickedMember}
            searchPlaceholder={t.searchMembersTablePlaceholder}
            translations={tableTranslations}
            showCopyRowId={viewerIsBackoffice}
          />
        </div>
      )
    }
  ];

  // Sits on the page background, not a card: muted-foreground is too light on the brand
  // gradient, so a translucent primary is used (same as the customer home header).
  const heading = (
    <div className='flex flex-col gap-1'>
      {!hideTitle && <h3 className='text-lg font-semibold'>{t.title}</h3>}
      <p className='max-w-2xl text-sm font-medium text-balance text-primary/75'>{t.description}</p>
    </div>
  );
  const showTab = (tab: ContactsSubTab) => {
    if (searchParams.get(CONTACTS_TAB_PARAM) === tab) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(CONTACTS_TAB_PARAM, tab);
    setSearchParams(nextParams, { replace: true });
  };
  // The new row is only visible on the member tab, so the add lands the admin there.
  const showMemberTab = () => showTab('members');
  // The warning banner counts cars, so its button has to land on the tab that lists them.
  const showCarTab = () => showTab('cars');
  const addAction = canManageMembers && (
    <AddOrganizationMemberDialog
      userGroupId={userGroupId}
      brandId={brandId}
      translations={t}
      showMutationToasts={showMutationToasts}
      onMemberAdded={showMemberTab}
    />
  );

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
        {heading}
        {addAction}
      </div>
      <NoddiBasicCard className='flex flex-col gap-4'>
        {canManageMembers && summary && (
          <OrganizationPrimaryContactForm
            key={summary.primaryContact?.userGroupMemberId ?? 'none'}
            userGroupId={userGroupId}
            primaryContact={summary.primaryContact ?? null}
            translations={t}
            showMutationToasts={showMutationToasts}
          />
        )}
        {summary && (
          <CarsNeedingAttentionBanner
            summary={summary}
            showOnlyNeedsAttention={showOnlyNeedsAttention}
            translations={t}
            onShowCars={() => {
              filterCarsNeedingAttention(true);
              showCarTab();
            }}
          />
        )}

        <NoddiTabs<ContactsSubTab>
          defaultValue='cars'
          searchParamName={CONTACTS_TAB_PARAM}
          tabItems={tabItems}
          keepExistingSearchParams
        />
      </NoddiBasicCard>
      <OrganizationContactOverlays
        userGroupId={userGroupId}
        currentUserId={currentUserId}
        translations={t}
        showMutationToasts={showMutationToasts}
        canManageMembers={canManageMembers}
        canAssignMembershipRole={canAssignMembershipRole}
        canEditMemberAccount={canEditMemberAccount}
        canRemoveMember={canRemove}
        getMemberDetailUrl={getMemberDetailUrl}
        onSelfRemoved={onSelfRemoved}
        adminCount={summary?.adminCount ?? 0}
        memberToEdit={memberToEdit || undefined}
        carToEdit={carToEdit}
        memberToRemove={memberToRemove}
        memberRemoveOpen={memberRemoveOpen}
        carToRemove={carToRemove}
        carRemoveOpen={carRemoveOpen}
        onCloseMember={() => setClickedMember(undefined)}
        onCloseCar={() => setCarToEdit(undefined)}
        // Keep the row mounted after close. Unmounting while the dialog is open leaves
        // Radix's scroll lock on the body, and the page stops taking clicks.
        onCloseRemoveMember={() => setMemberRemoveOpen(false)}
        onCloseRemoveCar={() => setCarRemoveOpen(false)}
        onRemoveMember={(member) => {
          setClickedMember(undefined);
          window.setTimeout(() => {
            setMemberToRemove(member);
            setMemberRemoveOpen(true);
          }, 0);
        }}
      />
    </div>
  );
};
